import { ApiFetch } from '../../../shared/utils/fetch'
import type { CustomFieldDefinition, IntakeDomain } from '../../orders/api/intakeApi'

/**
 * PE-1913/1915: field-config API client for the TEMPLATE-COPY model.
 *
 * The SOLUTION catalog is a TEMPLATE (TCS-admin authored). A merchant either has
 * NO config — the template applies verbatim — or its OWN fully-editable `fields`
 * list: typically the template copied, then edited freely (label, type, required,
 * select options, validation, order) and extended with merchant-specific fields.
 * Effective intake schema = fields ?? catalog.
 *
 * Backend contract (osprey-api):
 *   SOLUTION template (FieldConfigSolutionController, TCS-admin, no merchant in scope):
 *     GET  /field-config/solution/:domain?solutionId=  → { solutionId, domain, fields }
 *     PUT  /field-config/solution/:domain?solutionId=  body { fields }  → upsert
 *   MERCHANT config (FieldConfigController):
 *     GET    /merchants/:merchantId/field-config/:domain  → MerchantFieldConfigResponse
 *     PUT    /merchants/:merchantId/field-config/:domain  body { fields }  → saved
 *     DELETE /merchants/:merchantId/field-config/:domain  → reset (⇒ template applies)
 *   The merchant routes resolve `solutionId` server-side from the merchant's own record — the
 *   caller can't set it (a stale/mismatched value would silently target the wrong solution's
 *   template for that merchant), so no `solutionId` param is sent here.
 *
 * All calls go through the single osprey-api origin via ApiFetch (getOspreyApiBaseUrl).
 */

/** GET /merchants/:merchantId/field-config/:domain — template + merchant copy + computed effective. */
export interface MerchantFieldConfigResponse {
  merchantId: string
  solutionId: string
  domain: string
  /** The solution template (TCS-admin authored). */
  catalog: CustomFieldDefinition[]
  /** The merchant's own editable field list, or null ⇒ unconfigured (template applies verbatim). */
  fields: CustomFieldDefinition[] | null
  /** fields ?? catalog — what the customer sees. */
  effective: CustomFieldDefinition[]
}

/** Body for PUT /merchants/:merchantId/field-config/:domain. */
export interface SaveMerchantConfigBody {
  fields: CustomFieldDefinition[]
}

/** GET /field-config/solution/:domain — the solution template. */
export interface SolutionCatalogResponse {
  solutionId: string
  domain: string
  fields: CustomFieldDefinition[]
}

function merchantPath(merchantId: string, domain: IntakeDomain) {
  return `/merchants/${merchantId}/field-config/${domain}`
}

function solutionPath(solutionId: string, domain: IntakeDomain) {
  return `/field-config/solution/${domain}?solutionId=${encodeURIComponent(solutionId)}`
}

export const fieldConfigApi = {
  // ── Merchant config ─────────────────────────────────────────────────────────
  /** GET template + merchant fields (null ⇒ unconfigured) + effective for a (merchant, domain). */
  getMerchantConfig: (merchantId: string, domain: IntakeDomain) =>
    ApiFetch.get<MerchantFieldConfigResponse>(merchantPath(merchantId, domain)),

  /** PUT (upsert) the merchant's own field list. */
  saveMerchantConfig: (merchantId: string, domain: IntakeDomain, fields: CustomFieldDefinition[]) =>
    ApiFetch.legacy_put<MerchantFieldConfigResponse>(merchantPath(merchantId, domain), {
      fields,
    } satisfies SaveMerchantConfigBody),

  /** DELETE the merchant config ⇒ the solution template applies verbatim again. */
  resetMerchantConfig: (merchantId: string, domain: IntakeDomain) =>
    ApiFetch.del<void>(merchantPath(merchantId, domain)),

  // ── Solution template (TCS-admin) ─────────────────────────────────────────────
  /** GET the solution template for a domain. */
  getSolutionCatalog: (solutionId: string, domain: IntakeDomain) =>
    ApiFetch.get<SolutionCatalogResponse>(solutionPath(solutionId, domain)),

  /** PUT (upsert) the solution template for a domain. Body: { fields: [...] }. */
  saveSolutionCatalog: (solutionId: string, domain: IntakeDomain, fields: CustomFieldDefinition[]) =>
    ApiFetch.legacy_put<SolutionCatalogResponse>(solutionPath(solutionId, domain), { fields }),
}
